import { useQueryClient } from '@tanstack/react-query';
import type { NextRouter } from 'next/router';
import { useRouter } from 'next/router';
import React from 'react';

import { useSocket } from 'lib/socket/context';
import useSocketChannel from 'lib/socket/useSocketChannel';
import useSocketMessage from 'lib/socket/useSocketMessage';

function getSocketParams(router: NextRouter) {

  if (
    router.pathname === '/txs' &&
    (router.query.tab === 'validated' || router.query.tab === undefined) &&
    !router.query.block_number &&
    !router.query.page
  ) {
    return { topic: 'transactions:new_transaction' as const, event: 'transaction' as const };
  }

  if (router.pathname === '/') {
    return { topic: 'transactions:new_transaction' as const, event: 'transaction' as const };
  }

  if (
    router.pathname === '/txs' &&
    router.query.tab === 'pending' &&
    !router.query.block_number &&
    !router.query.page
  ) {
    return { topic: 'transactions:new_pending_transaction' as const, event: 'pending_transaction' as const };
  }

  return {};
}

function assertIsNewTxResponse(response: unknown): response is { transaction: number } {
  return typeof response === 'object' && response !== null && 'transaction' in response;
}
function assertIsNewPendingTxResponse(response: unknown): response is { pending_transaction: number } {
  return typeof response === 'object' && response !== null && 'pending_transaction' in response;
}

function isTxListQueryKey(key: unknown): boolean {
  return (
    key === 'homepage_txs' ||
    key === 'homepage_txs_watchlist' ||
    key === 'txs_validated' ||
    key === 'txs_pending'
  );
}

export default function useNewTxsSocket() {
  const router = useRouter();
  const queryClient = useQueryClient();
  const socket = useSocket();
  const [ socketAlert, setSocketAlert ] = React.useState('');

  const { topic, event } = getSocketParams(router);

  const refreshTxQueries = React.useCallback(() => {
    return queryClient.invalidateQueries({
      predicate: (q) => isTxListQueryKey(q.queryKey[0]),
    });
  }, [ queryClient ]);

  const handleNewTxMessage = React.useCallback((response: { transaction: number } | { pending_transaction: number } | unknown) => {
    setSocketAlert('');

    if (assertIsNewTxResponse(response) || assertIsNewPendingTxResponse(response)) {
      void refreshTxQueries();
    }
  }, [ refreshTxQueries ]);

  const handleSocketClose = React.useCallback(() => {
    setSocketAlert('Connection was lost. Refreshing transactions…');
    void refreshTxQueries().finally(() => {
      setSocketAlert('');
    });
  }, [ refreshTxQueries ]);

  const handleSocketError = React.useCallback(() => {
    setSocketAlert('Could not reach live updates. Refreshing transactions…');
    void refreshTxQueries().finally(() => {
      setSocketAlert('');
    });
  }, [ refreshTxQueries ]);

  React.useEffect(() => {
    if (!topic) {
      return;
    }

    const ref = socket?.onOpen(() => {
      setSocketAlert('');
    });

    return () => {
      if (ref && socket) {
        socket.off([ ref ]);
      }
    };
  }, [ socket, topic ]);

  const channel = useSocketChannel({
    topic,
    onSocketClose: handleSocketClose,
    onSocketError: handleSocketError,
    isDisabled: !topic,
  });

  useSocketMessage({
    channel,
    event,
    handler: handleNewTxMessage,
  });

  if (!topic && !event) {
    return {};
  }

  return { num: 0, socketAlert };
}
